import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import BusinessIcon from '@mui/icons-material/Business';
import { useUserContext } from '../../../hooks/UserContext';
import {
  ClearSearchIcon,
  CompanyFilterTitle,
  CompanySelectorModalWrapper,
  FilterCompanyContainer,
  FilterCompanyWrapper,
  PaginationButton,
  PaginationLeft,
  PaginationRight,
  PaginationTitle,
  SearchInput,
  SearchInputWrapper,
  SelectorLable,
  SelectorModalPaginationWrapper,
  SelectorWrapper,
  Tag,
  UserName,
  UserRole,
} from './navbar.style';
import { useCompany } from '../../../hooks/CompanyContext';
import * as CompanyService from '../../../services/bioplus-services/company';
import { Label, Modal } from '../../../Components/uielements';
import { RightArrowPrimary, RightArrowDisabled, ChevronDownWhite } from '../../../Components/icons';
import { IntlMessages } from '../../../helper/langHelper';

const useDebouncedValue = (value, delay = 400) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

const CompanySelectorModal = ({
  onClose,
  onSelect,
  nextPage,
  prevPage,
  data,
  page,
  pageSize,
  isPagination,
  selectedCompanyId,
  searchTerm,
  onSearchChange,
  isSearching,
}) => {
  const companyList = data?.data?.data ?? [];
  const total = data?.data?.total ?? 0;
  const maxPages = Math.ceil(total / pageSize);
  const start = total === 0 ? 0 : page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, total);
  const hasPrev = page > 0;
  const hasNext = page + 1 < maxPages;

  return (
    <>
      <Modal
        isOpen={true}
        onClose={onClose}
        title={<IntlMessages id="nav.drop.down.role.select.label" />}
      >
        <SearchInputWrapper>
          <SearchInput
            type="text"
            placeholder="Search entity..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            autoFocus
          />{' '}
          {searchTerm && <ClearSearchIcon onClick={() => onSearchChange('')}>✕</ClearSearchIcon>}
        </SearchInputWrapper>

        <CompanySelectorModalWrapper>
          {isSearching ? (
            <SelectorLable>
              <IntlMessages id="loading.text" />
            </SelectorLable>
          ) : companyList && companyList?.length > 0 ? (
            <>
              {companyList.map((company) => (
                <SelectorWrapper key={company.id} onClick={() => onSelect && onSelect(company)}>
                  <SelectorLable isSeleted={selectedCompanyId === company?._id}>
                    {company?.name}
                    {selectedCompanyId === company?._id && <Tag>Selected</Tag>}
                  </SelectorLable>
                </SelectorWrapper>
              ))}
            </>
          ) : (
            <SelectorLable>
              {searchTerm ? 'No companies found' : <IntlMessages id="loading.text" />}
            </SelectorLable>
          )}
        </CompanySelectorModalWrapper>
        {isPagination ? (
          <SelectorModalPaginationWrapper>
            <PaginationTitle>
              {start}-{end} <IntlMessages id="nav.of" /> {total}
            </PaginationTitle>{' '}
            <div style={{ display: 'grid', gridAutoFlow: 'column', gap: '20px' }}>
              <PaginationLeft onClick={hasPrev ? prevPage : null}>
                {hasPrev ? (
                  <img
                    src={RightArrowPrimary}
                    alt="RightArrowPrimary.svg"
                    style={{ transform: 'rotate(180deg)', width: '11px', height: '11px' }}
                  />
                ) : (
                  <img
                    src={RightArrowDisabled}
                    alt="RightArrowPrimary.svg"
                    style={{ transform: 'rotate(180deg)', width: '11px', height: '11px' }}
                  />
                )}
                <PaginationButton color="lightGreen" cursor="pointer" disabled={!hasPrev}>
                  <IntlMessages id="nav.drop.down.previous" />
                </PaginationButton>
              </PaginationLeft>{' '}
              <PaginationButton
                color="lightGreen"
                cursor="pointer"
                onClick={() => onSelect(undefined)}
              >
                <IntlMessages id="nav.drop.down.all" />
              </PaginationButton>
              <PaginationRight onClick={hasNext ? nextPage : null}>
                <PaginationButton color="lightGreen" cursor="pointer" disabled={!hasNext}>
                  <IntlMessages id="nav.drop.down.next" />
                </PaginationButton>
                {hasNext ? (
                  <img
                    src={RightArrowPrimary}
                    alt="RightArrowPrimary.svg"
                    style={{ width: '11px', height: '11px' }}
                  />
                ) : (
                  <img
                    src={RightArrowDisabled}
                    alt="RightArrowPrimary.svg"
                    style={{ width: '11px', height: '11px' }}
                  />
                )}
              </PaginationRight>
            </div>
          </SelectorModalPaginationWrapper>
        ) : null}
      </Modal>
    </>
  );
};

const PartnerCompanyFilter = () => {
  const companyContext = useCompany();
  const userContext = useUserContext();
  const company = companyContext?.company;
  const [selectedCompany, setSelectedCompany] = useState(false);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [pageSize, setPageSize] = useState(10);

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 400);

  const query = useQuery(
    ['partnerCompanies', page, pageSize, debouncedSearchTerm],
    () =>
      CompanyService.companyFilterList({
        userType: 'PARTNER',
        page,
        pageSize,
        companyName: debouncedSearchTerm || undefined,
      }),
    {
      keepPreviousData: true,
    }
  );

  const isSearching = query.isFetching && searchTerm !== debouncedSearchTerm;

  const handleSelected = () => {
    setSelectedCompany(true);
  };

  const handleSelectedCompany = (company) => {
    companyContext.set(company?._id);
    setSelectedCompany(false);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPage(0);
  };

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const handleClose = () => {
    setSelectedCompany(false);
    setSearchTerm('');
    setPage(0);
  };

  return (
    <>
      <FilterCompanyContainer onClick={handleSelected}>
        <FilterCompanyWrapper>
          <div style={{ display: 'grid', gap: '4px' }}>
            <CompanyFilterTitle transform="capitalize">
              <IntlMessages id="nav.drop.down.companyFilter" />
            </CompanyFilterTitle>
            <UserRole>
              {company?.name ? company?.name : <IntlMessages id="nav.drop.down.all" />}
            </UserRole>
          </div>
          <ChevronDownWhite />
        </FilterCompanyWrapper>
      </FilterCompanyContainer>
      {selectedCompany && (
        <CompanySelectorModal
          onClose={handleClose}
          onSelect={handleSelectedCompany}
          nextPage={handleNextPage}
          prevPage={handlePreviousPage}
          data={query}
          page={page}
          pageSize={pageSize}
          isPagination={true}
          selectedCompanyId={companyContext?.company?._id}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          isSearching={isSearching}
        />
      )}
    </>
  );
};

const AdminCompanyFilter = () => {
  const companyContext = useCompany();
  const userContext = useUserContext();
  const company = companyContext?.company;
  const [selectedCompany, setSelectedCompany] = useState(false);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [pageSize, setPageSize] = useState(10);

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 400);

  const query = useQuery(
    ['companyFilter', page, pageSize, debouncedSearchTerm],
    () =>
      CompanyService.companyFilterList({
        userType: 'ADMIN',
        page,
        pageSize,
        companyName: debouncedSearchTerm || undefined,
      }),
    {
      keepPreviousData: true,
    }
  );

  const isSearching = query.isFetching && searchTerm !== debouncedSearchTerm;

  const handleSelected = () => {
    setSelectedCompany(true);
  };

  const handleSelectedCompany = (company) => {
    companyContext.set(company?._id);
    setSelectedCompany(false);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPage(0);
  };

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const handleClose = () => {
    setSelectedCompany(false);
    setSearchTerm('');
    setPage(0);
  };

  return (
    <>
      <FilterCompanyContainer onClick={handleSelected}>
        <FilterCompanyWrapper>
          <div style={{ display: 'grid', gap: '4px' }}>
            <CompanyFilterTitle transform="capitalize">
              <IntlMessages id="nav.drop.down.companyFilter" />
            </CompanyFilterTitle>
            <UserRole>
              {company?.name ? company?.name : <IntlMessages id="nav.drop.down.all" />}
            </UserRole>
          </div>
          <ChevronDownWhite />
        </FilterCompanyWrapper>
      </FilterCompanyContainer>
      {selectedCompany && (
        <CompanySelectorModal
          onClose={handleClose}
          onSelect={handleSelectedCompany}
          nextPage={handleNextPage}
          prevPage={handlePreviousPage}
          data={query}
          page={page}
          pageSize={pageSize}
          isPagination={true}
          selectedCompanyId={companyContext?.company?._id}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          isSearching={isSearching}
        />
      )}
    </>
  );
};

const CorporateAdminCompanyFilter = () => {
  return (
    <FilterCompanyContainer>
      <FilterAltIcon />
      <div style={{ display: 'grid', gap: '10px' }}>
        <UserName transform="capitalize">
          <IntlMessages id="nav.drop.down.companyFilter" />
        </UserName>
        <UserRole>
          <IntlMessages id="nav.drop.down.all" />
        </UserRole>
      </div>
    </FilterCompanyContainer>
  );
};

// Main Component to render the filter option based on the role

const CompanyFilter = () => {
  const userContext = useUserContext();

  switch (userContext?.company?.role) {
    case 'ADMIN':
      return <AdminCompanyFilter />;
    case 'PARTNER':
      return <PartnerCompanyFilter />;
    case 'CORPORATE_ADMIN':
      return <CorporateAdminCompanyFilter />;
    default:
      return null;
  }
};

export default CompanyFilter;
